import moment from 'moment-timezone';

import { Event } from '../interfaces';
import { hourToString } from './describeHoursRange';

// Cached per locale rather than once at module load: a moment takes the global locale when
// it is constructed, so day names cached up front would keep whichever language happened to
// be active the first time this module was evaluated. Keying on the locale keeps a language
// change correct while sparing seven moment constructions per call -- `getEnforcementPeriods`
// runs twice per event details row and once per event in a CSV export.
const dayNamesByLocale = new Map<string, Map<number, string>>();

const dayIntToStringMap = (shortForm: boolean): Map<number, string> => {
  const format = shortForm ? 'dd' : 'dddd';
  const cacheKey = `${moment.locale()}:${format}`;
  const cached = dayNamesByLocale.get(cacheKey);

  if (cached) {
    return cached;
  }

  const dayNames = new Map(Array.from({ length: 7 }, (_, day): [number, string] => [day, moment().day(day).format(format)]));
  dayNamesByLocale.set(cacheKey, dayNames);

  return dayNames;
};

// Returns null when the event carries no enforce-time information, so callers can
// render a localized "unspecified" rather than a yes/no answer.
export const withinEnforcementTime = (event: Event): boolean | null => event?.within_enforce_time ?? null;

export const getEnforcementPeriods = (event: Event, shortForm = true): string[] => {
  const enforcementDetailsForBlockFiltered =
    event?.metadata?.route_metadata?.enforcement_info?.filter((ei) => ei.event_types?.includes(event?.event_type_identifier)) || [];

  const filteredSource =
    enforcementDetailsForBlockFiltered.length === 0 ? [event?.metadata?.route_metadata] : enforcementDetailsForBlockFiltered;

  const periodsArray = [] as string[];

  const dayNames = dayIntToStringMap(shortForm);

  for (let i = 0; i < filteredSource?.length; i++) {
    const enforcementDaysArray = filteredSource[i]?.enforce_days || [];
    const enforceTimeStart = filteredSource[i]?.enforce_start;
    const enforceTimeEnd = filteredSource[i]?.enforce_end;

    const enforce2TimeStart = filteredSource[i]?.enforce2_start ?? 0;
    const enforce2TimeEnd = filteredSource[i]?.enforce2_end ?? 24;

    if (enforcementDaysArray.length === 0 && filteredSource.length === 1) {
      periodsArray.push('Skip');
      return periodsArray;
    }

    if (enforceTimeStart === 0 && enforceTimeEnd === 24 && filteredSource.length === 1) {
      periodsArray.push('24h');
      return periodsArray;
    }

    if (!enforceTimeStart && !enforceTimeEnd && filteredSource.length === 1) {
      periodsArray.push('None');
      return periodsArray;
    }
    // sort to ensure 0 -> 6 order
    enforcementDaysArray.sort();
    let enforcementDays = '';
    if (enforcementDaysArray.length === 1) {
      const dayString = dayNames.get(enforcementDaysArray[0]);
      return dayString ? [dayString] : [];
    }
    let isChain = false;
    for (let j = 0; j <= enforcementDaysArray.length - 1; j++) {
      if (j === enforcementDaysArray.length - 1) {
        enforcementDays = `${enforcementDays} ${dayNames.get(enforcementDaysArray[j])}`;
      }
      if (isChain) {
        if (enforcementDaysArray[j + 1] - enforcementDaysArray[j] > 1) {
          isChain = false;
        }
      }
      if (!isChain) {
        enforcementDays += dayNames.get(enforcementDaysArray[j]);
        if (enforcementDaysArray[j + 1] - enforcementDaysArray[j] === 1) {
          enforcementDays += ' - ';
          isChain = true;
        } else {
          enforcementDays += ', ';
          isChain = false;
        }
      }
    }

    let secondEnforceTime = '';
    if (enforce2TimeStart && enforce2TimeEnd) {
      secondEnforceTime = ` and ${hourToString(enforce2TimeStart)} - ${hourToString(enforce2TimeEnd)}`;
    }

    periodsArray.push(`${enforcementDays}, ${hourToString(enforceTimeStart)} - ${hourToString(enforceTimeEnd)}${secondEnforceTime}`);

    // loop ends here
  }
  return periodsArray;
};

export const isInEnforcementZone = (event: Event): boolean => Boolean(event?.within_enforce_zone);
